import { ConfirmationResult, RecaptchaVerifier } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { authManager } from "../../data/managers/AuthManager";
import { useAuth } from "../../data/providers/AuthProvider";
import { authService } from "../../firebase-config";
import { normalizePhone } from "../../utils/StringTool";

const EMAIL_LOGIN_STATE_KEY = "convisas_email_login_state_v1";
const EMAIL_LOGIN_STATE_TTL_MS = 15 * 60 * 1000;

type EmailLoginState = {
  email: string;
  passport: string;
  step: string;
  savedAt: number;
};

function getStoredEmailLoginState(): EmailLoginState | null {
  try {
    const raw = localStorage.getItem(EMAIL_LOGIN_STATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as EmailLoginState;
  } catch {
    return null;
  }
}

function saveEmailLoginState(payload: EmailLoginState) {
  try {
    localStorage.setItem(EMAIL_LOGIN_STATE_KEY, JSON.stringify(payload));
  } catch (error) {
    console.warn("No se pudo guardar el estado temporal del login:", error);
  }
}

function clearEmailLoginState() {
  try {
    localStorage.removeItem(EMAIL_LOGIN_STATE_KEY);
  } catch (error) {
    console.warn("No se pudo limpiar el estado temporal del login:", error);
  }
}

declare global {
  interface Window {
    recaptchaVerifier?: RecaptchaVerifier;
  }
}

export function useLogin({ country }: any) {
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [passport, setPassport] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("input");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingWhatsapp, setIsSubmittingWhatsapp] = useState(false);
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [isLogged, setLogged] = useState(false);
  const recaptchaContainerRef = useRef(null);
  const [confirmationResult, setConfirmationResult] =
    useState<ConfirmationResult | null>(null);

  const { user } = useAuth();

  const isPhoneValid = phone.length >= 7 && /^[0-9]+$/.test(phone);
  const isCodeValid = code.length >= 6;
  const countryCode = country?.code ?? "+57";

  useEffect(() => {
    const saved = getStoredEmailLoginState();
    if (!saved) return;
    if (!saved.savedAt || Date.now() - saved.savedAt > EMAIL_LOGIN_STATE_TTL_MS) {
      clearEmailLoginState();
      return;
    }
    if (saved.email) setEmail(saved.email);
    if (saved.passport) setPassport(saved.passport);
    if (saved.step === "codeEmail") setStep("codeEmail");
  }, []);

  useEffect(() => {
    setLogged(!!user);
  }, [user]);

  useEffect(() => {
    if (step !== "input" && step !== "codeEmail") return;
    if (!email && !passport && step === "input") {
      clearEmailLoginState();
      return;
    }
    const payload: EmailLoginState = {
      email,
      passport,
      step,
      savedAt: Date.now(),
    };
    saveEmailLoginState(payload);
  }, [email, passport, step]);

  useEffect(() => {
    if (!recaptchaContainerRef.current) return;
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        authService,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => {
            (window.recaptchaVerifier as any)?.recaptcha?.reset();
          },
          "expired-callback": () => {
            (window.recaptchaVerifier as any)?.recaptcha?.reset();
          },
          "error-callback": () => {
            (window.recaptchaVerifier as any)?.recaptcha?.reset();
          },
        }
      );
    }

    return () => {
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {}
        delete window.recaptchaVerifier;
      }
    };
  }, []);

  const handleSendCodeBySms = async (e?: any) => {
    e?.preventDefault();
    if (!isPhoneValid) return;
    setIsSubmitting(true);
    setError("");
    try {
      const appVerifier = window.recaptchaVerifier;

      if (!appVerifier) {
        throw new Error(
          "Cannot use invisible captcha. Please reload the page."
        );
      }
      const result = await authManager.sendSmsCode(
        `${countryCode}${phone}`,
        appVerifier
      );
      if (!result) {
        throw new Error("No fue posible iniciar la verificacion SMS.");
      }
      setConfirmationResult(result);
      setStep("code");
    } catch (error) {
      console.log(error);
      setError("Error enviando código SMS");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCodeSms = async (e: any) => {
    e.preventDefault();
    if (!isCodeValid) return;
    setIsSubmitting(true);
    setError("");
    try {
      await authManager.loginWithPhone(confirmationResult ?? undefined, code);
      setLogged(true);
    } catch (error) {
      setError("Código incorrecto");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendCodeByWhatsapp = async (e: any) => {
    e.preventDefault();
    if (!isPhoneValid) return;
    setIsSubmittingWhatsapp(true);
    setError("");
    try {
      const normalized = normalizePhone(countryCode, phone);
      await authManager.sendWhatsappCode(normalized);
      setStep("codeWhatsapp");
    } catch (error) {
      console.log(error);
      setError("Error enviando código SMS");
    } finally {
      setIsSubmittingWhatsapp(false);
    }
  };

  const handleVerifyCodeByWhatsapp = async (e: any) => {
    e.preventDefault();
    if (!isCodeValid) return;
    setIsSubmitting(true);
    setError("");
    try {
      const normalized = normalizePhone(countryCode, phone);
      await authManager.loginWithWhatsapp({
        phoneNumber: normalized,
        code,
      });
      setLogged(true);
    } catch (error) {
      setError("Código incorrecto");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendCodeByEmail = async (e: any) => {
    e.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    const normalizedPassport = passport.trim().toUpperCase();
    if (!normalizedEmail || !normalizedPassport) {
      setError("Ingresa correo y pasaporte para enviar el codigo.");
      return;
    }
    setIsSubmittingEmail(true);
    setError("");
    try {
      await authManager.sendEmailCode(normalizedEmail, normalizedPassport);
      setStep("codeEmail");
    } catch (error: any) {
      setError(error.message ?? "Error enviando código Email");
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  const handleVerifyCodeByEmail = async (e: any) => {
    e.preventDefault();
    if (!isCodeValid) return;
    setIsSubmittingEmail(true);
    setError("");
    try {
      await authManager.loginWithEmail({
        email: email.trim().toLowerCase(),
        code,
        passport: passport.trim().toUpperCase(),
      });
      clearEmailLoginState();
      setLogged(true);
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === "string"
            ? error
            : "Código incorrecto";
      setError(message || "Código incorrecto");
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  return {
    phone,
    setPhone,
    email,
    setEmail,
    passport,
    setPassport,
    code,
    setCode,
    step,
    setStep,
    error,
    setError,
    isSubmitting,
    setIsSubmitting,
    isSubmittingWhatsapp,
    setIsSubmittingWhatsapp,
    isSubmittingEmail,
    setIsSubmittingEmail,
    isLogged,
    setLogged,
    isPhoneValid,
    isCodeValid,
    handleSendCodeBySms,
    handleVerifyCodeSms,
    handleSendCodeByWhatsapp,
    handleVerifyCodeByWhatsapp,
    handleSendCodeByEmail,
    handleVerifyCodeByEmail,
    recaptchaContainerRef,
  };
}
